import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { subDays } from 'date-fns';
import { useEffect } from 'react';

export interface BaseScorePerformanceResult {
  guard_id?: string;
  guard_name?: string;
  base_score: number;
  operational_activity_score: number;
  supervisor_reports_score: number;
  client_evaluation_score: number;
  behavior_score: number;
  punctuality_score: number;
  final_score: number;
  performance_grade: string;
  latest_ovit_score: number;
  operational_activity_details: any;
  supervisor_reports_details: any;
  client_evaluation_details: any;
  behavior_details: any;
  punctuality_details: any;
  // Distinction fields for performance categories that exceed targets
  operational_distinction: boolean;
  supervisor_distinction: boolean;
  client_distinction: boolean;
  behavior_distinction: boolean;
  punctuality_distinction: boolean;
  // Legacy fields for backward compatibility
  patrol_activity_points: number;
  supervisor_reports_points: number;
  client_evaluation_points: number;
  behavior_points: number;
  punctuality_points: number;
  emergency_reports_points: number;
  patrol_observations_points: number;
  patrol_observations_count: number;
  details: any;
}

// Hook για υπολογισμό performance με βασικό σκορ
export const useBaseScorePerformanceEvaluation = (
  guardId: string,
  startDate?: string,
  endDate?: string,
  realTime = true
) => {
  const queryClient = useQueryClient();
  // Use tomorrow as default end date to include incidents scheduled for future dates
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultEndDate = tomorrow.toISOString().split('T')[0];
  const defaultStartDate = subDays(new Date(), 30).toISOString().split('T')[0];

  // Force the end date to be tomorrow if not specified
  const finalEndDate = endDate || defaultEndDate;
  const finalStartDate = startDate || defaultStartDate;

  console.log('Performance hook - using dates:', { finalStartDate, finalEndDate, guardId });

  const queryKey = ['base-score-performance', guardId, finalStartDate, finalEndDate];

  // Real-time subscription for performance updates
  useEffect(() => {
    if (!realTime || !guardId) return;
    
    console.log('Setting up real-time subscription for guard:', guardId);
    
    const channel = supabase
      .channel('performance-updates-' + guardId)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'behavior_records', filter: `guard_id=eq.${guardId}` },
        (payload) => {
          console.log('Behavior record change detected:', payload);
          queryClient.invalidateQueries({ queryKey });
          queryClient.refetchQueries({ queryKey });
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'supervisor_reports', filter: `guard_id=eq.${guardId}` },
        (payload) => {
          console.log('Supervisor report change detected:', payload);
          queryClient.invalidateQueries({ queryKey });
          queryClient.refetchQueries({ queryKey });
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'client_evaluations', filter: `guard_id=eq.${guardId}` },
        (payload) => {
          console.log('Client evaluation change detected:', payload);
          queryClient.invalidateQueries({ queryKey });
          queryClient.refetchQueries({ queryKey });
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'ovit_evaluations', filter: `guard_id=eq.${guardId}` },
        (payload) => {
          console.log('OVIT evaluation change detected:', payload);
          queryClient.invalidateQueries({ queryKey });
          queryClient.refetchQueries({ queryKey });
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'clock_in_out', filter: `user_id=eq.${guardId}` },
        (payload) => {
          console.log('Clock in/out change detected:', payload);
          queryClient.invalidateQueries({ queryKey });
          queryClient.refetchQueries({ queryKey });
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'guard_locations', filter: `guard_id=eq.${guardId}` },
        (payload) => {
          console.log('Guard location change detected:', payload);
          queryClient.invalidateQueries({ queryKey });
          queryClient.refetchQueries({ queryKey });
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'emergency_reports', filter: `guard_id=eq.${guardId}` },
        (payload) => {
          console.log('Emergency report change detected:', payload);
          queryClient.invalidateQueries({ queryKey });
          queryClient.refetchQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up real-time subscription for guard:', guardId);
      supabase.removeChannel(channel);
    };
  }, [guardId, realTime, queryClient, queryKey]);

  return useQuery({
    queryKey,
    queryFn: async (): Promise<BaseScorePerformanceResult> => {
      console.log('=== PERFORMANCE QUERY DEBUG ===');
      console.log('Guard ID:', guardId);
      console.log('Start Date:', finalStartDate);
      console.log('End Date:', finalEndDate);
      console.log('Query Key:', queryKey);
      console.log('================================');
      
      const { data, error } = await supabase.rpc('calculate_guard_performance_with_base_score', {
        p_guard_id: guardId,
        p_start_date: finalStartDate,
        p_end_date: finalEndDate,
      });

      if (error) {
        console.error('Error calculating base score performance:', error);
        throw error;
      }

      console.log('Raw performance data with dates', finalStartDate, 'to', finalEndDate, ':', data);
      
      if (!data || data.length === 0) {
        console.log('No performance data returned');
        throw new Error('No performance data returned');
      }

      // Type assertion to handle the database response format
      const result = data[0] as any;
      console.log('Parsed result with final_score:', result.final_score);
      
      const finalResult: BaseScorePerformanceResult = {
        guard_id: result.guard_id,
        base_score: result.base_score || 0,
        final_score: result.final_score || 0,
        performance_grade: result.performance_grade || 'F',
        operational_activity_score: result.operational_activity_score || 0,
        supervisor_reports_score: result.supervisor_reports_score || 0,
        client_evaluation_score: result.client_evaluation_score || 0,
        behavior_score: result.behavior_score || 0,
        punctuality_score: result.punctuality_score || 0,
        operational_distinction: result.operational_distinction || false,
        supervisor_distinction: result.supervisor_distinction || false,
        client_distinction: result.client_distinction || false,
        behavior_distinction: result.behavior_distinction || false,
        punctuality_distinction: result.punctuality_distinction || false,
        latest_ovit_score: result.latest_ovit_score || 0,
        operational_activity_details: result.operational_activity_details || result.details?.operational_activity_details || {},
        supervisor_reports_details: result.supervisor_reports_details || {},
        client_evaluation_details: result.client_evaluation_details || {},
        behavior_details: result.behavior_details || {},
        punctuality_details: result.punctuality_details || {},
        // Legacy fields for backward compatibility
        patrol_activity_points: result.operational_activity_score || 0,
        supervisor_reports_points: result.supervisor_reports_score || 0,
        client_evaluation_points: result.client_evaluation_score || 0,
        behavior_points: result.behavior_score || 0,
        punctuality_points: result.punctuality_points ?? result.punctuality_score ?? 5,
        emergency_reports_points: 0,
        patrol_observations_points: 0,
        patrol_observations_count: 0,
        details: result.details || {}
      };
      
      console.log('=== FINAL RESULT DEBUG ===');
      console.log('Final Score:', finalResult.final_score);
      console.log('Behavior Score:', finalResult.behavior_score);
      console.log('Performance Grade:', finalResult.performance_grade);
      console.log('==========================');
      return finalResult;
    },
    enabled: !!guardId,
    staleTime: 30000, // 30 seconds - reduced from always stale
    refetchOnWindowFocus: true,
  });
};

// Hook για real-time calculation χωρίς αποθήκευση
export const useCalculateBaseScorePerformance = () => {
  return useMutation({
    mutationFn: async ({
      guardId,
      startDate,
      endDate,
    }: {
      guardId: string;
      startDate: string;
      endDate: string;  
    }) => {
      const { data, error } = await supabase.rpc('calculate_guard_performance_with_base_score', {
        p_guard_id: guardId,
        p_start_date: startDate,
        p_end_date: endDate,
      });

      if (error) {
        console.error('Error calculating performance:', error);
        throw error;
      }

      return data?.[0];
    },
    onSuccess: () => {
      toast.success('Υπολογισμός απόδοσης ολοκληρώθηκε!');
    },
    onError: (error: any) => {
      console.error('Performance calculation failed:', error);
      toast.error(`Σφάλμα υπολογισμού: ${error.message || 'Άγνωστο σφάλμα'}`);
    },
  });
};

// Hook για πρόσφατες αξιολογήσεις όλων των υπαλλήλων
export const useAllGuardsBaseScorePerformance = (
  teamId?: string,
  startDate?: string,
  endDate?: string,
  realTime = true
) => {
  const queryClient = useQueryClient();
  // Use tomorrow as default end date to include incidents scheduled for future dates
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultEndDate = tomorrow.toISOString().split('T')[0];
  const defaultStartDate = subDays(new Date(), 30).toISOString().split('T')[0];

  const queryKey = ['all-guards-base-performance-v2', teamId, startDate || defaultStartDate, endDate || defaultEndDate];
  
  // Real-time subscription for team performance updates
  useEffect(() => {
    if (!realTime || !teamId) return;
    
    const channel = supabase
      .channel('team-performance-updates-' + teamId)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'supervisor_reports' },
        () => queryClient.invalidateQueries({ queryKey: ['all-guards-base-performance-v2'] })
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'behavior_records' },
        () => queryClient.invalidateQueries({ queryKey: ['all-guards-base-performance-v2'] })
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'client_evaluations' },
        () => queryClient.invalidateQueries({ queryKey: ['all-guards-base-performance-v2'] })
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'ovit_evaluations' },
        () => queryClient.invalidateQueries({ queryKey: ['all-guards-base-performance-v2'] })
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'clock_in_out' },
        () => queryClient.invalidateQueries({ queryKey: ['all-guards-base-performance-v2'] })
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'guard_locations' },
        () => queryClient.invalidateQueries({ queryKey: ['all-guards-base-performance-v2'] })
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'emergency_reports' },
        () => queryClient.invalidateQueries({ queryKey: ['all-guards-base-performance-v2'] })
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'team_members', filter: `team_id=eq.${teamId}` },
        () => queryClient.invalidateQueries({ queryKey: ['all-guards-base-performance-v2'] })
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [teamId, realTime, queryClient, queryKey]);

  return useQuery({
    queryKey,
    queryFn: async (): Promise<(BaseScorePerformanceResult & { guard_id: string; guard_name: string })[]> => {
      // Get all team members first
      let guardsQuery = supabase
        .from('team_members')
        .select(`
          user_id,
          profiles:profiles!user_id(
            first_name,
            last_name,
            full_name,
            status
          )
        `)
        .neq('profiles.status', 'archived');

      if (teamId) {
        guardsQuery = guardsQuery.eq('team_id', teamId);
      }

      const { data: guards, error: guardsError } = await guardsQuery;

      if (guardsError) throw guardsError;

      if (!guards || guards.length === 0) {
        return [];
      }

      // Filter to include only guards (users who either have 'guard' role or no admin/manager/client roles)
      const filteredGuards = [];
      
      for (const guard of guards) {
        if (!guard.profiles) continue;
        
        // Get role from profiles table instead of user_roles
        const { data: profileData } = await supabase
          .from('profiles')
          .select('Role')
          .eq('id', guard.user_id)
          .single();

        const userRole = profileData?.Role;
        
        // Only include users who have the 'guard' role
        const isGuard = userRole === 'guard';
        
        if (isGuard) {
          filteredGuards.push(guard);
        }
      }

      // Unique guards only
      const uniqueGuards = filteredGuards.filter((guard, index, self) => 
        guard.profiles && 
        index === self.findIndex(g => g.user_id === guard.user_id)
      );

      // Υπολογίζουμε performance για κάθε guard
      const results = await Promise.allSettled(
        uniqueGuards.map(async (guard) => {
          const { data, error } = await supabase.rpc('calculate_guard_performance_with_base_score', {
            p_guard_id: guard.user_id,
            p_start_date: startDate || defaultStartDate,
            p_end_date: endDate || defaultEndDate,
          });

          if (error) {
            console.warn(`Error calculating performance for guard ${guard.user_id}:`, error);
            return null;
          }

          if (!data || data.length === 0) {
            return null;
          }

          const result = data[0] as any;
          
          // Check if guard has a Shiftask evaluation for current semester
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();
          const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';
          
          const { data: shiftaskEval } = await supabase
            .from('ovit_evaluations')
            .select('id')
            .eq('guard_id', guard.user_id)
            .eq('status', 'active')
            .eq('evaluation_year', currentYear)
            .eq('evaluation_period', currentSemester)
            .maybeSingle();
          
          const hasShiftaskEvaluation = !!shiftaskEval;
          
          // Process data similar to single guard hook
          return {
            guard_id: guard.user_id,
            guard_name: guard.profiles.full_name || 
              `${guard.profiles.first_name} ${guard.profiles.last_name}`,
            base_score: result.base_score || 0,
            operational_activity_score: result.operational_activity_score || 0,
            supervisor_reports_score: result.supervisor_reports_score || 0,
            client_evaluation_score: result.client_evaluation_score || 0,
            behavior_score: result.behavior_score || 0,
            punctuality_score: result.punctuality_score || 0,
            final_score: result.final_score || 0,
            performance_grade: result.performance_grade || 'F',
            latest_ovit_score: result.latest_ovit_score || 0,
            operational_activity_details: result.operational_activity_details || {},
            supervisor_reports_details: result.supervisor_reports_details || {},
            client_evaluation_details: result.client_evaluation_details || {},
            behavior_details: result.behavior_details || {},
            punctuality_details: result.punctuality_details || {},
            operational_distinction: result.operational_distinction || false,
            supervisor_distinction: result.supervisor_distinction || false,
            client_distinction: result.client_distinction || false,
            behavior_distinction: result.behavior_distinction || false,
            punctuality_distinction: result.punctuality_distinction || false,
            // Legacy fields for backward compatibility
            patrol_activity_points: result.operational_activity_score || 0,
            supervisor_reports_points: result.supervisor_reports_score || 0,
            client_evaluation_points: result.client_evaluation_score || 0,
            behavior_points: result.behavior_score || 0,
            punctuality_points: result.punctuality_score ?? result.punctuality_points ?? 5,
            emergency_reports_points: 0,
            patrol_observations_points: 0,
            patrol_observations_count: 0,
            details: {
              patrol_activities: 0,
              emergency_reports: 0,
              patrol_observations_count: 0,
              supervisor_reports_count: 0,
              client_evaluations_count: 0,
              behavior_records_count: 0,
              positive_behaviors: 0,
              negative_behaviors: 0,
              total_shifts: 0,
              has_ovit_evaluation: result.latest_ovit_score > 0,
              has_shiftask_evaluation: hasShiftaskEvaluation,
              ovit_base_score: result.base_score || 0,
            }
          };
        })
      );

      // Φιλτράρουμε τα επιτυχημένα αποτελέσματα
      return results
        .filter((result): result is PromiseFulfilledResult<any> => 
          result.status === 'fulfilled' && result.value !== null
        )
        .map(result => result.value)
        .sort((a, b) => b.final_score - a.final_score); // Ταξινόμηση από υψηλότερο σκορ
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};